import React from 'react';
import { Helmet } from 'react-helmet-async';

const CANONICAL_ORIGIN = 'https://www.alert-1.com';

function getCanonicalPath(path: string): string {
  const pathname = new URL(path, CANONICAL_ORIGIN).pathname || '/';

  if (pathname === '/') {
    return pathname;
  }

  return pathname.replace(/\/+$/, '');
}

export function getAbsoluteUrl(path: string): string {
  return `${CANONICAL_ORIGIN}${getCanonicalPath(path)}`;
}

interface OpenGraphHeadProps {
  title: string;
  description: string;
  imagePath?: string;
  pathOverride?: string;
  imageAlt?: string;
  noIndex?: boolean;
}

export const OpenGraphHead: React.FC<OpenGraphHeadProps> = ({
  title,
  description,
  imagePath = '/logo512.png',
  pathOverride,
  imageAlt = 'Alert1 Medical Alert Systems',
  noIndex = false,
}) => {
  const path = pathOverride ?? (typeof window !== 'undefined' ? window.location.pathname : '/');
  const url = getAbsoluteUrl(path);
  const imageUrl = imagePath.startsWith('http') ? imagePath : getAbsoluteUrl(imagePath);

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      <meta name="robots" content={noIndex ? 'noindex,nofollow' : 'index,follow'} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Alert1" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />
      <meta name="twitter:image:alt" content={imageAlt} />
    </Helmet>
  );
};

export default OpenGraphHead;
